<template>
  <div>
    <!-- 手機版頂部導航 -->
    <div class="mobile-header" v-if="isMobile">
      <div class="mobile-logo">
        <router-link to="/home">
          <img
            src="/platform_logo.svg"
            :alt="t('sidebar.taiwanSmartHealthSchool')"
          />
        </router-link>
      </div>
      <div class="mobile-header-buttons">
        <!-- 漢堡選單按鈕 -->
        <div class="mobile-hamburger-button" @click="toggleNavigationMenu">
          <MenuOutlined v-if="!navigationMenuOpen" />
          <CloseOutlined v-else />
        </div>
        <!-- 使用者選單按鈕 -->
        <div class="mobile-profile-button" @click="toggleUserMenu">
          <div class="profile-icon-circle">
            <img
              v-if="user && user.avatar"
              :src="user.avatar"
              :alt="user.name || '用戶頭像'"
              class="user-avatar"
            />
            <div v-else class="user-avatar-placeholder">
              <UserOutlined />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 手機版使用者選單 -->
    <MobileMenu
      v-if="isMobile && userMenuOpen"
      :is-open="userMenuOpen"
      @update:is-open="userMenuOpen = $event"
      @menu-click="handleUserMenuClick"
    />

    <!-- 手機版導航選單 -->
    <MobileNavigationMenu
      v-if="isMobile && navigationMenuOpen"
      :is-open="navigationMenuOpen"
      @update:is-open="navigationMenuOpen = $event"
      @menu-click="handleNavigationMenuClick"
    />

    <!-- 桌面版左側導航欄 -->
    <div class="sidebar" v-if="!isMobile">
      <div class="nav-container">
        <a-menu mode="inline" :selectedKeys="selectedMenuKeys">
          <a-menu-item key="dashboard" @click="navigateTo('/dashboard')">
            <template #icon>
              <img
                class="menu-icon"
                src="/dashboard/dashboard.svg"
                alt="dashboard icon"
              />
            </template>
            {{ t("sidebar.dashboard") }}
          </a-menu-item>
          <a-menu-item key="certificates" @click="navigateTo('/Certificate')">
            <template #icon>
              <img
                class="menu-icon"
                src="/dashboard/folder.svg"
                alt="certificate icon"
              />
            </template>
            {{ t("sidebar.certificates") }}
          </a-menu-item>
          <a-menu-item key="favorites" @click="navigateTo('/favorites')">
            <template #icon>
              <img class="menu-icon" src="/bookmark.png" alt="favorite icon" />
            </template>
            {{ t("sidebar.favoriteCourses") }}
          </a-menu-item>
        </a-menu>
      </div>

      <div class="user-section">
        <button type="button" class="user-trigger" @click="toggleAccountMenu">
          <div class="user-summary">
            <div class="user-avatar-placeholder desktop-user-avatar">
              <UserOutlined />
            </div>
            <span class="user-name" v-if="user">{{ user.name }}</span>
            <span class="user-name" v-else>{{ t("sidebar.user") }}</span>
          </div>
          <span class="user-trigger-arrow" :class="{ open: accountMenuOpen }">
            <DownOutlined />
          </span>
        </button>
        <div v-if="accountMenuOpen" class="user-action-menu">
          <button
            type="button"
            class="user-menu-link"
            :class="{ active: route.path === '/ProfileEdit' && (!route.query.tab || route.query.tab === 'profile') }"
            @click="navigateTo('/ProfileEdit?tab=profile')"
          >
            帳號設定
          </button>
          <button
            type="button"
            class="user-menu-link"
            :class="{ active: route.path === '/ProfileEdit' && route.query.tab === 'security' }"
            @click="navigateTo('/ProfileEdit?tab=security')"
          >
            變更密碼
          </button>
          <button type="button" class="user-menu-link" @click="handleLogout">
            登出
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mobile-header-buttons {
  display: flex;
  justify-content: flex-end; /* 靠右對齊 */
  align-items: center;
  gap: 2px;
}

.mobile-profile-button {
  cursor: pointer;
  line-height: 32px;
  transform: translateY(-5px);
}

.profile-icon-circle {
  width: 32px;
  height: 32px;
  background: var(--color-primary-active);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.mobile-hamburger-button {
  cursor: pointer;
  padding: 0 8px;
  font-size: 22px;
  line-height: 40px;
  color: #333;
  display: none;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .mobile-hamburger-button {
    display: block;
  }

  .profile-icon-circle {
    transform: translateY(5px);
  }
}

/* 桌面版專用 */
.desktop-only {
  display: block;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
}
</style>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import {
  UserOutlined,
  MenuOutlined,
  CloseOutlined,
  DownOutlined,
} from "@ant-design/icons-vue";
import MobileMenu from "./MobileMenu.vue";
import MobileNavigationMenu from "./MobileNavigationMenu.vue";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const user = ref(null);

// RWD 相關狀態
const isMobile = ref(window.innerWidth <= 768);
const userMenuOpen = ref(false);
const navigationMenuOpen = ref(false);
const accountMenuOpen = ref(false);

// 切換使用者選單
const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
  // 關閉另一個選單
  if (userMenuOpen.value) {
    navigationMenuOpen.value = false;
  }
};

// 切換導航選單
const toggleNavigationMenu = () => {
  navigationMenuOpen.value = !navigationMenuOpen.value;
  // 關閉另一個選單
  if (navigationMenuOpen.value) {
    userMenuOpen.value = false;
  }
};

const toggleAccountMenu = () => {
  accountMenuOpen.value = !accountMenuOpen.value;
};

// 處理使用者選單點擊
const handleUserMenuClick = () => {
  userMenuOpen.value = false;
};

// 處理導航選單點擊
const handleNavigationMenuClick = () => {
  navigationMenuOpen.value = false;
};

// 導航到指定頁面並關閉兩個選單
const navigateTo = (path) => {
  router.push(path);
  userMenuOpen.value = false;
  navigationMenuOpen.value = false;
  accountMenuOpen.value = false;
};

// 監聽視窗大小變化
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
  if (!isMobile.value) {
    userMenuOpen.value = false;
    navigationMenuOpen.value = false;
  }
};

// 監聽 sessionStorage 變化並處理用戶資料
const handleUserDataChange = () => {
  try {
    const cachedUserData = sessionStorage.getItem("user_data");

    if (cachedUserData) {
      const parsedData = JSON.parse(cachedUserData);

      // 檢查是否有 content 欄位
      if (parsedData.content) {
        // 如果有 content，只抓 content
        user.value = parsedData.content;
      } else {
        // 如果沒有 content，抓全部資料
        user.value = parsedData;
      }
    } else {
      // 如果 sessionStorage 沒有資料，清空 user
      user.value = null;
    }
  } catch (error) {
    console.error("處理用戶資料錯誤:", error);
    user.value = null;
  }
};

// 監聽 storage 事件（同一個頁面內的變化）
const handleStorageChange = (event) => {
  if (event.key === "user_data") {
    handleUserDataChange();
  }
};

// 手動觸發檢查（用於初始化和手動更新）
const checkUserData = () => {
  handleUserDataChange();
};

const selectedMenuKeys = computed(() => {
  if (route.path.startsWith("/Certificate")) {
    return ["certificates"];
  }

  if (
    route.path.startsWith("/favorites") ||
    route.path.startsWith("/FavoriteList")
  ) {
    return ["favorites"];
  }

  if (
    route.path.startsWith("/dashboard") ||
    route.path.startsWith("/Dashboard")
  ) {
    return ["dashboard"];
  }

  return [];
});

const handleLogout = () => {
  // 清除 sessionStorage
  localStorage.removeItem("ms_platform_token");
  sessionStorage.removeItem("user_data");

  // 重定向到登入頁面
  window.location.href = "/sign-in";
  message.success("已成功登出");
};

onMounted(() => {
  // 添加視窗大小變化監聽
  window.addEventListener("resize", handleResize);

  // 添加 storage 事件監聽（跨頁面變化）
  window.addEventListener("storage", handleStorageChange);

  // 初始檢查用戶資料
  checkUserData();
});

onUnmounted(() => {
  // 移除視窗大小變化監聽
  window.removeEventListener("resize", handleResize);
  // 移除 storage 事件監聽
  window.removeEventListener("storage", handleStorageChange);
});
</script>


<style>
/* 整體應用容器 */
.app-container {
  display: flex;
  min-height: calc(100vh - 58px);
}

/* 左側導航欄 */
.sidebar {
  width: 220px;
  padding: 32px 16px 24px;
  background: linear-gradient(0deg, #c4c4c4, #c4c4c4),
    linear-gradient(182.03deg, #084843 1.71%, #00908e 98.29%);
  background-blend-mode: multiply;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 58px;
  left: 0;
  height: calc(100vh - 58px);
  overflow-y: auto;
  z-index: 10;
}

.nav-section {
  margin-top: 16px;
}

.nav-container .ant-menu {
  background-color: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  padding: 0;
}

.nav-container .ant-menu-item {
  color: #fff;
  height: 52px;
  margin: 0 0 12px !important;
  padding: 0 16px !important;
  border-radius: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.nav-container
  .ant-menu-item:not(.ant-menu-item-selected):hover
  .ant-menu-title-content {
  color: #fff;
}

.nav-container .ant-menu-item::after {
  display: none;
}

.nav-container .ant-menu-item.ant-menu-item-selected {
  background: #e0f1f2 !important;
  color: var(--color-primary) !important;
}

.nav-container .ant-menu-item .ant-menu-title-content,
.nav-container .ant-menu-item-selected .ant-menu-title-content {
  color: inherit;
}

.nav-container .ant-menu-item:not(.ant-menu-item-selected):hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.menu-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.nav-container .ant-menu-item-selected .menu-icon {
  filter: brightness(0) saturate(100%) invert(28%) sepia(20%) saturate(1714%)
    hue-rotate(129deg) brightness(92%) contrast(96%);
}

.user-section {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.24);
}

.user-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.user-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
  min-width: 0;
}

.user-name {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  line-height: 1.4;
  word-break: break-word;
  text-align: left;
}

.user-avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.18);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.desktop-user-avatar {
  flex-shrink: 0;
}

.user-trigger-arrow {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  transition: transform 0.2s ease;
}

.user-trigger-arrow.open {
  transform: rotate(180deg);
}

.user-action-menu {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0 0 52px;
}

.user-menu-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
}

.user-menu-link.active,
.user-menu-link:hover {
  color: #e0f1f2;
}

/* 手機版導航樣式 */
.mobile-header {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-logo {
  display: flex;
  align-items: center;
}

.mobile-logo img {
  max-height: 45px;
}

.mobile-menu-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu {
  position: fixed;
  top: 60px;
  left: 0;
  width: 100%;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 99;
  padding-bottom: 16px;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
}

.mobile-menu .ant-menu-item svg {
  fill: #333;
}

.mobile-menu .ant-menu-item.ant-menu-item-selected svg {
  fill: var(--color-primary);
}

.mobile-content {
  margin-left: 0;
  padding-top: 80px; /* 為手機版頂部導航留出空間 */
}

/* RWD */
@media (max-width: 768px) {
  .app-container {
    display: block;
  }

  .sidebar {
    display: none;
  }

  .mobile-header {
    display: flex;
  }

  .main-content {
    margin-left: 0;
    padding: 76px 16px 20px;
  }

  .two-column-layout {
    flex-direction: column;
  }

  .right-column {
    width: 100%;
  }

  .info-cards-section {
    flex-direction: column;
    gap: 12px;
  }

  .info-card {
    height: 80px;
  }

  .info-card::after {
    display: none;
  }

  .progress-bar-container {
    width: 60px;
  }

  .welcome-section h1 {
    font-size: 20px;
  }

  .welcome-section p {
    font-size: 14px;
  }

  .section-title {
    font-size: 16px;
  }
}
</style>
